import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Award, Users, Building2, GraduationCap, FileText, ExternalLink,
  Download, CheckCircle, BookOpen, ClipboardList, Calendar,
  UserCheck, Microscope, Star, Clock, MessageSquare, Landmark, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NMC_HERO = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80";
const DEAN_PHOTO = "https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/v0kgpsrn_Dean%20Dr%20Kasar%202.jpg";
const MS_PHOTO = "https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/1nkjr0s1_Dr%20Arujun%20Saxena.jpeg";

const SECTION_ICONS = [
  UserCheck, Users, GraduationCap, Award, Building2, ClipboardList,
  BookOpen, Star, ClipboardList, Calendar, Star, Clock,
  UserCheck, BookOpen, Building2, MessageSquare, Microscope, Landmark
];

function PdfLink({ label, url, testId }) {
  if (!url) return <span className="text-sm text-gray-400 italic">PDF not yet uploaded</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#4ECDC4] hover:text-[#3DBDB4] font-medium text-sm transition-colors group" data-testid={testId}>
      <FileText size={16} className="shrink-0" />
      <span className="group-hover:underline">{label}</span>
      <ExternalLink size={12} className="shrink-0 opacity-60" />
    </a>
  );
}

function Placeholder() {
  return (
    <div className="flex items-center gap-3 py-4 px-5 bg-gray-50 rounded-lg border border-dashed border-gray-200">
      <Clock size={18} className="text-gray-400 shrink-0" />
      <p className="text-sm text-gray-500">To be updated. Content will be available soon.</p>
    </div>
  );
}

const NMCDisclosures = () => {
  const [data, setData] = useState(null);
  const [staffCount, setStaffCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/cms/pages/nmc_mandatory`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
    fetch(`${API}/api/cms/teaching-staff`)
      .then(r => r.json())
      .then(d => setStaffCount((d.staff || []).length))
      .catch(() => {});
  }, []);

  const s = data?.sections || {};

  return (
    <div className="page-transition" data-testid="nmc-disclosures-page">
      {/* Hero */}
      <section
        className="relative py-20 md:py-32 hero-overlay"
        style={{ backgroundImage: `url(${NMC_HERO})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="nmc-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              NMC Compliance
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Mandatory Information
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Complete institutional disclosures as mandated by the National Medical Commission for transparency in medical education.
            </p>
          </div>
        </div>
      </section>

      {/* Recognition Banner */}
      <section className="py-6 bg-gradient-to-r from-[#4ECDC4] to-[#3DBDB4]" data-testid="recognition-banner">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 text-white">
              <CheckCircle size={28} />
              <div>
                <p className="font-heading text-lg font-semibold">NMC Recognized Institution</p>
                <p className="text-white/80 text-sm">Sukh Sagar Medical College & Hospital, Block- Bargi, Jabalpur 482003</p>
              </div>
            </div>
            <a href="https://www.nmc.org.in/information-desk/for-students-to-study-in-india/list-of-college-teaching-mbbs/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="bg-white text-[#4ECDC4] hover:bg-gray-100 rounded-full px-6 font-medium" data-testid="verify-nmc-btn">
                <ExternalLink className="mr-2" size={16} />Verify on NMC Website
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* 18 Sections Accordion */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="mandatory-sections">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">

              {/* 1. Dean & Medical Superintendent */}
              <AccordionItem value="s1" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-1">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">1</span><span className="font-heading font-semibold">Dean & Medical Superintendent</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border border-gray-100 shadow-none"><CardContent className="p-5">
                      <div className="flex gap-4 items-start">
                        <img src={DEAN_PHOTO} alt="Dean" className="w-20 h-24 rounded-lg object-cover object-[25%_top] shrink-0" />
                        <div>
                          <p className="font-heading font-bold text-[#1A202C]">Dr. Pradeep Kumar Kasar</p>
                          <p className="text-xs text-gray-500">MBBS, MD (Community Medicine)</p>
                          <p className="text-sm text-[#D4A84B] font-semibold mt-1">Dean, SSMCH</p>
                          <p className="text-xs text-gray-400 mt-1">Since 1st July 2022</p>
                          <p className="text-xs text-gray-400">dean@ssmch.in</p>
                        </div>
                      </div>
                    </CardContent></Card>
                    <Card className="border border-gray-100 shadow-none"><CardContent className="p-5">
                      <div className="flex gap-4 items-start">
                        <img src={MS_PHOTO} alt="Medical Superintendent" className="w-20 h-24 rounded-lg object-cover object-top shrink-0" />
                        <div>
                          <p className="font-heading font-bold text-[#1A202C]">Dr. Arjun Saxena</p>
                          <p className="text-xs text-gray-500">MBBS, MS (Gen. Surgery), PhD, FICS</p>
                          <p className="text-sm text-[#4ECDC4] font-semibold mt-1">Medical Superintendent</p>
                          <p className="text-xs text-gray-400 mt-1">Since 3rd August 2022</p>
                          <p className="text-xs text-gray-400">drarjun.saxena@ssmch.in</p>
                        </div>
                      </div>
                    </CardContent></Card>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 2. Teaching & Non-Teaching Staff */}
              <AccordionItem value="s2" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-2">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">2</span><span className="font-heading font-semibold">Teaching & Non-Teaching Staff</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4 space-y-3">
                  <Link to="/teaching-staff" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group" data-testid="teaching-staff-link">
                    <Users size={20} className="text-[#4ECDC4]" /><span className="text-sm font-medium text-[#1A202C] group-hover:text-[#4ECDC4]" data-testid="teaching-staff-link-label">Teaching Staff{staffCount != null ? ` (${staffCount} Faculty Member${staffCount === 1 ? '' : 's'})` : ''}</span><ChevronRight size={14} className="ml-auto text-gray-400" />
                  </Link>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <PdfLink label="Non-Teaching Staff" url={s.non_teaching_staff_pdf} testId="non-teaching-pdf" />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 3. Sanctioned Courses */}
              <AccordionItem value="s3" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-3">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">3</span><span className="font-heading font-semibold">Sanctioned Courses</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <p className="text-gray-700 text-sm leading-relaxed">{s.sanctioned_courses || "MBBS — Sukh Sagar Medical College and Hospital Jabalpur offers 150 seats in the M.B.B.S course, which lasts for four and a half years and a compulsory internship of 12 months."}</p>
                </AccordionContent>
              </AccordionItem>

              {/* 4. Recognition Status */}
              <AccordionItem value="s4" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-4">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">4</span><span className="font-heading font-semibold">Recognition Status</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4 space-y-3">
                  <p className="text-gray-700 text-sm leading-relaxed">{s.recognition_text || "Recognized by National Medical Commission by LOP Dated 06.12.2021"}</p>
                  <a href={s.recognition_verify_url || "https://www.nmc.org.in/information-desk/for-students-to-study-in-india/list-of-college-teaching-mbbs/"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#4ECDC4] hover:text-[#3DBDB4] font-medium text-sm" data-testid="verify-nmc-link">
                    <CheckCircle size={16} />Verify on NMC Website<ExternalLink size={12} />
                  </a>
                </AccordionContent>
              </AccordionItem>

              {/* 5. University Details */}
              <AccordionItem value="s5" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-5">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">5</span><span className="font-heading font-semibold">University Details</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4 space-y-4">
                  <div>
                    <p className="text-gray-700 text-sm font-medium mb-1">{s.university_name || "Affiliated to MPMSU (Madhya Pradesh Medical Science University), Jabalpur"}</p>
                    <a href={s.university_url || "https://mpmsu.edu.in/Affiliation"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#4ECDC4] text-sm"><ExternalLink size={12} />View Affiliation</a>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border border-gray-100 shadow-none"><CardContent className="p-4">
                      <p className="font-semibold text-sm text-[#1A202C] mb-2">Vice Chancellor</p>
                      <p className="text-sm text-gray-700">{s.vc_name || "Dr. Ashok Khandelwal"}</p>
                      <p className="text-xs text-gray-500 mt-1">Tel: {s.vc_phone || "0761-2670340"}</p>
                      <p className="text-xs text-gray-500">Email: {s.vc_email || "vcmpmsu@mp.gov.in"}</p>
                    </CardContent></Card>
                    <Card className="border border-gray-100 shadow-none"><CardContent className="p-4">
                      <p className="font-semibold text-sm text-[#1A202C] mb-2">Registrar</p>
                      <p className="text-sm text-gray-700">{s.registrar_name || "Dr. Pushpraj Singh Baghel"}</p>
                      <p className="text-xs text-gray-500 mt-1">Tel: {s.registrar_phone || "0761-2670332"}</p>
                      <p className="text-xs text-gray-500">Email: {s.registrar_email || "registrarmpmsu@mp.gov.in"}</p>
                    </CardContent></Card>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 6. Students Details */}
              <AccordionItem value="s6" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-6">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">6</span><span className="font-heading font-semibold">Students Details</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4 space-y-5">
                  <div>
                    <p className="font-semibold text-sm text-[#1A202C] mb-3">MBBS Batch Details</p>
                    <div className="space-y-2">
                      {(s.mbbs_batches || [
                        { label: "MBBS Batch: 2021-2022", pdf: null },
                        { label: "MBBS Batch: 2022-2023", pdf: null },
                        { label: "MBBS Batch: 2023-2024", pdf: null },
                        { label: "MBBS Batch: 2024-2025", pdf: null },
                      ]).map((b, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <PdfLink label={b.label} url={b.pdf} testId={`batch-${i}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-[#1A202C] mb-3">Stipend Paid</p>
                    <div className="space-y-2">
                      {(s.stipend_details || [
                        { label: "Stipend Details 2023-24", pdf: null },
                        { label: "Stipend Details 2024-25", pdf: null },
                        { label: "Stipend Details 2025-26", pdf: null },
                      ]).map((st, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <PdfLink label={st.label} url={st.pdf} testId={`stipend-${i}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 7. Clinical Material */}
              <AccordionItem value="s7" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-7">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">7</span><span className="font-heading font-semibold">Clinical Material</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <PdfLink label="Updated Clinical Material" url={s.clinical_material_pdf} testId="clinical-material-pdf" />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 8. Results */}
              <AccordionItem value="s8" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-8">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">8</span><span className="font-heading font-semibold">Results</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.results_items?.length > 0 ? s.results_items.map((r, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={r.label} url={r.pdf} testId={`result-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 9. Committees */}
              <AccordionItem value="s9" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-9">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">9</span><span className="font-heading font-semibold">Committees</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(s.committees || [
                      { label: "Anti Ragging Committee", pdf: null },
                      { label: "College Council Committee", pdf: null },
                      { label: "Curriculum Committee", pdf: null },
                      { label: "Gender Harassment Cell", pdf: null },
                      { label: "Grievance Redressal Cell", pdf: null },
                      { label: "Hostel Monitoring Committee", pdf: null },
                      { label: "Hospital Infection Control Committee", pdf: null },
                      { label: "Medical Education Unit Committee", pdf: null },
                      { label: "Staff Scientific Committee", pdf: null },
                    ]).map((c, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg">
                        <PdfLink label={c.label} url={c.pdf} testId={`committee-${i}`} />
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 10. CME, Conferences */}
              <AccordionItem value="s10" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-10">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">10</span><span className="font-heading font-semibold">CME, Conferences & Academic Activities</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <PdfLink label="Know More" url={s.cme_pdf} testId="cme-pdf" />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 11. Awards & Achievements */}
              <AccordionItem value="s11" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-11">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">11</span><span className="font-heading font-semibold">Awards & Achievements</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.awards_items?.length > 0 ? s.awards_items.map((a, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={a.label} url={a.pdf} testId={`award-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 12. Time Table */}
              <AccordionItem value="s12" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-12">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">12</span><span className="font-heading font-semibold">Time Table</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.timetable_items?.length > 0 ? s.timetable_items.map((t, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={t.label} url={t.pdf} testId={`timetable-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 13. Faculty Attendance */}
              <AccordionItem value="s13" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-13">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">13</span><span className="font-heading font-semibold">Faculty Attendance</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <a href={s.faculty_attendance_url || "https://ssmchjab.nmcindia.ac.in/"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group" data-testid="attendance-link">
                    <UserCheck size={20} className="text-[#4ECDC4]" /><span className="text-sm font-medium text-[#1A202C] group-hover:text-[#4ECDC4]">Attendance</span><ExternalLink size={14} className="ml-auto text-gray-400" />
                  </a>
                </AccordionContent>
              </AccordionItem>

              {/* 14. Teaching Details */}
              <AccordionItem value="s14" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-14">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">14</span><span className="font-heading font-semibold">Teaching Details</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.teaching_items?.length > 0 ? s.teaching_items.map((t, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={t.label} url={t.pdf} testId={`teaching-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 15. Infrastructure */}
              <AccordionItem value="s15" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-15">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">15</span><span className="font-heading font-semibold">Infrastructure</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.infrastructure_items?.length > 0 ? s.infrastructure_items.map((inf, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={inf.label} url={inf.pdf} testId={`infra-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 16. Student Feedback */}
              <AccordionItem value="s16" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-16">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">16</span><span className="font-heading font-semibold">Student Feedback</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.feedback_items?.length > 0 ? s.feedback_items.map((f, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={f.label} url={f.pdf} testId={`feedback-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

              {/* 17. Research Publications */}
              <AccordionItem value="s17" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-17">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">17</span><span className="font-heading font-semibold">Research Publications</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  <div className="space-y-2">
                    {(s.publications || [
                      { label: "Publication of General Medicine Department", pdf: null },
                    ]).map((p, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg">
                        <PdfLink label={p.label} url={p.pdf} testId={`publication-${i}`} />
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 18. Citizen Charter */}
              <AccordionItem value="s18" className="bg-white rounded-xl shadow-sm border-0 overflow-hidden" data-testid="section-18">
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-[#1A202C] [&[data-state=open]]:text-white">
                  <span className="flex items-center gap-3 text-left"><span className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center shrink-0 text-[#4ECDC4] text-xs font-bold">18</span><span className="font-heading font-semibold">Citizen Charter</span></span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-4">
                  {s.citizen_charter_items?.length > 0 ? s.citizen_charter_items.map((c, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg mb-2"><PdfLink label={c.label} url={c.pdf} testId={`charter-${i}`} /></div>
                  )) : <Placeholder />}
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>
      </section>

      {/* Compliance Notice */}
      <section className="py-12 bg-white" data-testid="compliance-notice">
        <div className="container-custom">
          <Card className="border-0 shadow-lg bg-gradient-to-r from-[#D4A84B]/10 to-[#4ECDC4]/10 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shrink-0 shadow-md">
                  <Award className="text-[#D4A84B]" size={28} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-2">Regulatory Compliance</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    SSMCH maintains full compliance with all regulations prescribed by the National Medical Commission (NMC) and the state government. All information provided on this page is updated regularly as per NMC guidelines for transparency in medical education.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default NMCDisclosures;
